import axios from 'axios';
import { getTokenData, refreshAccessToken, removeTokenData, saveTokenData } from './auth/AuthService';
import { REACT_APP_BACKEND_API_URL } from '../utils/config';
import { encodeModelAliasesInParams, encodeModelAliasesInUrl } from '../utils/modelUrlAlias';

let isRefreshing = false;
let isLoggingOut = false;
let failedQueue: { resolve: (token: string) => void; reject: (err: any) => void }[] = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token!);
    }
  });
  failedQueue = [];
};

// Przypisanie do window.location tylko PLANUJE nawigację — JS leci dalej, więc React zdąży się
// przerenderować z już wyczyszczonym tokenem. Wiszący promise zostawia wywołujących w stanie
// ładowania zamiast wpychać im błąd albo pusty wynik, na którym render mógłby paść.
const forceLogout = (): Promise<never> => {
  if (!isLoggingOut) {
    isLoggingOut = true;
    // Oczekujące żądania zostają zawieszone razem z bieżącym — patrz wyżej.
    failedQueue = [];
    removeTokenData();
    window.location.href = '/login';
  }

  return new Promise<never>(() => {});
};

// A ban can land mid-session (the automatic abuse ban answers the offending save with 403
// `Account banned`). apiClient lives outside the React tree, so AccountProvider registers here what
// to do: refetch the account, after which PrivateRoutes swaps the app for BannedPage.
let accountBannedHandler: (() => void) | null = null;
export const setAccountBannedHandler = (handler: (() => void) | null) => {
  accountBannedHandler = handler;
};

export const client = (isJSONType: boolean, isAPI: boolean, sentCookie: boolean = false) => {
  const baseURL = `${REACT_APP_BACKEND_API_URL}${isAPI ? '/api' : ''}`;
  const contentType = isJSONType ? 'application/json' : 'application/x-www-form-urlencoded';

  let headers = {
    'Content-Type': contentType,
  };

  if (isAPI) {
    const tokenData = getTokenData();
    headers = {
      ...headers,
      ...(tokenData ? { Authorization: `Bearer ${tokenData.accessToken}` } : {}),
    };
  }

  const instance = axios.create({
    baseURL,
    headers,
    timeout: 120_000,
    maxContentLength: Infinity,
    maxBodyLength: Infinity,
    withCredentials: sentCookie
  });

  // Nazwy modeli w ścieżce i w `?models=` kodujemy tuż przed wysyłką — patrz `modelUrlAlias`.
  // Jedno miejsce dla wszystkich żądań, bo składa je kilkanaście serwisów.
  instance.interceptors.request.use((config) => {
    if (config.url) {
      config.url = encodeModelAliasesInUrl(config.url);
    }
    encodeModelAliasesInParams(config.params);
    return config;
  });

  if (isAPI) {
    instance.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;

        if (error.response?.status === 403 && error.response.data?.error === 'Account banned') {
          accountBannedHandler?.();
        }

        if (error.response && error.response.status === 401 && !originalRequest._retry) {
          originalRequest._retry = true;

          // Kolejna fala żądań (np. refetch react-query po powrocie do zakładki) trafiająca tu już po
          // decyzji o wylogowaniu nie ma czego odświeżać — token jest usunięty, a nawigacja w drodze.
          if (isLoggingOut) {
            return forceLogout();
          }

          if (isRefreshing) {
            return new Promise((resolve, reject) => {
              failedQueue.push({
                resolve: (token: string) => {
                  originalRequest.headers['Authorization'] = `Bearer ${token}`;
                  resolve(instance(originalRequest));
                },
                reject,
              });
            });
          }

          isRefreshing = true;

          try {
            const newTokenData = await refreshAccessToken();

            if (!newTokenData) {
              throw new Error('Token refresh failed');
            }

            saveTokenData(newTokenData);
            processQueue(null, newTokenData.accessToken);

            originalRequest.headers['Authorization'] = `Bearer ${newTokenData.accessToken}`;
            return instance(originalRequest);
          } catch (err) {
            console.error('Token refresh failed — logging out', err);
            return forceLogout();
          } finally {
            isRefreshing = false;
          }
        }

        return Promise.reject(error);
      }
    );
  }

  return instance;
};
